import { useEffect, useRef } from 'react'
import { Empty } from 'antd'
import { MessageSquare } from 'lucide-react'
import type { Message as MessageType } from '@/types/chat.types'
import { Message } from '../Message'
import { t } from '@/config/translations'
import styles from './MessageList.module.css'

export interface MessageListProps {
  messages: MessageType[]
  isStreaming?: boolean
  onSendMessage?: (query: string) => void
}

export function MessageList({ messages, isStreaming = false, onSendMessage }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const userScrolledUpRef = useRef(false)

  // Auto-scroll to bottom when new messages arrive or streaming updates
  useEffect(() => {
    // Don't auto-scroll if user scrolled up manually
    if (userScrolledUpRef.current && !isStreaming) {
      return
    }

    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isStreaming])

  // Detect user scroll
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100

      // If user is near bottom, consider them not scrolled up
      userScrolledUpRef.current = !isNearBottom
    }

    container.addEventListener('scroll', handleScroll)
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Reset scroll tracking when streaming starts
  useEffect(() => {
    if (isStreaming) {
      userScrolledUpRef.current = false
    }
  }, [isStreaming])

  if (messages.length === 0) {
    return (
      <div className={styles['emptyContainer']}>
        <Empty
          image={<MessageSquare size={64} strokeWidth={1} style={{ color: '#bfbfbf' }} />}
          styles={{ image: { height: 80 } }}
          description={t('no_messages')}
        />
      </div>
    )
  }

  return (
    <div ref={containerRef} className={styles['messageList']}>
      {messages.map((message, index) => (
        <Message
          key={message.id}
          message={message}
          isStreaming={isStreaming && index === messages.length - 1}
          onSendMessage={onSendMessage}
        />
      ))}
      <div ref={messagesEndRef} />
    </div>
  )
}
